/**
 * ShiftCalendarView.js - Visual Calendar for Worker Shift Scheduling
 * Features: Drag-and-drop shifts, preset templates, visual week grid
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronLeft, ChevronRight, Plus, X, Clock, User, Calendar,
  Trash2, Check, RefreshCw, Users, Sun, Moon, Sunset, Coffee, Copy, GripVertical
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import api from '../../services/api';

// Shift type colors
const SHIFT_COLORS = {
  regular: { bg: 'bg-blue-500', text: 'text-white', light: 'bg-blue-100', border: 'border-blue-500' },
  overtime: { bg: 'bg-orange-500', text: 'text-white', light: 'bg-orange-100', border: 'border-orange-500' },
  on_call: { bg: 'bg-purple-500', text: 'text-white', light: 'bg-purple-100', border: 'border-purple-500' },
};

// Shift Templates with preset times
const SHIFT_TEMPLATES = {
  morning: { 
    name: 'Morning', 
    start_time: '06:00', 
    end_time: '14:00', 
    icon: Sun, 
    color: 'bg-amber-500',
    description: '6 AM - 2 PM (8h)'
  },
  day: { 
    name: 'Day', 
    start_time: '09:00', 
    end_time: '17:00', 
    icon: Coffee, 
    color: 'bg-blue-500',
    description: '9 AM - 5 PM (8h)'
  },
  evening: { 
    name: 'Evening', 
    start_time: '14:00', 
    end_time: '22:00', 
    icon: Sunset, 
    color: 'bg-orange-500',
    description: '2 PM - 10 PM (8h)'
  },
  night: { 
    name: 'Night', 
    start_time: '22:00', 
    end_time: '06:00', 
    icon: Moon, 
    color: 'bg-indigo-600',
    description: '10 PM - 6 AM (8h)'
  },
};

const ShiftCalendarView = () => {
  // State
  const [workers, setWorkers] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(today.setDate(diff)).toISOString().split('T')[0];
  });
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingShift, setEditingShift] = useState(null);
  const [shiftForm, setShiftForm] = useState({
    worker_id: '',
    date: '',
    start_time: '09:00',
    end_time: '17:00',
    shift_type: 'regular',
    notes: ''
  });
  
  // Drag state
  const [draggedShift, setDraggedShift] = useState(null);
  const [dragOverCell, setDragOverCell] = useState(null);

  // Calculate week days
  const weekDays = useMemo(() => {
    const days = [];
    const start = new Date(currentWeekStart);
    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      days.push({
        date: date.toISOString().split('T')[0],
        dayName: date.toLocaleDateString('en-US', { weekday: 'short' }),
        dayNum: date.getDate(),
        isToday: date.toISOString().split('T')[0] === new Date().toISOString().split('T')[0]
      });
    }
    return days;
  }, [currentWeekStart]);

  // Week navigation
  const navigateWeek = (direction) => {
    const current = new Date(currentWeekStart);
    current.setDate(current.getDate() + (direction * 7));
    setCurrentWeekStart(current.toISOString().split('T')[0]);
  };

  const goToToday = () => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1);
    setCurrentWeekStart(new Date(today.setDate(diff)).toISOString().split('T')[0]);
  };

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const weekEnd = new Date(currentWeekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      
      const [workersRes, shiftsRes] = await Promise.all([
        api.get('/workers'),
        api.get('/schedules/shifts', {
          params: {
            start_date: currentWeekStart,
            end_date: weekEnd.toISOString().split('T')[0]
          }
        })
      ]);
      
      setWorkers(workersRes.data || []);
      setShifts(shiftsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch shift data:', error);
      toast.error('Failed to load shift data');
    } finally {
      setLoading(false);
    }
  }, [currentWeekStart]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Get shift for a specific worker and date
  const getShiftForCell = (workerId, date) => {
    return shifts.find(s => s.worker_id === workerId && s.date === date);
  };

  // Apply template to form
  const applyTemplate = (templateKey) => {
    const template = SHIFT_TEMPLATES[templateKey];
    if (template) {
      setShiftForm(prev => ({
        ...prev,
        start_time: template.start_time,
        end_time: template.end_time
      }));
      toast.success(`Applied ${template.name} shift template`);
    }
  };

  // Open modal to add shift
  const openAddShiftModal = (workerId = '', date = '') => {
    setEditingShift(null);
    setShiftForm({
      worker_id: workerId,
      date: date || currentWeekStart,
      start_time: '09:00',
      end_time: '17:00',
      shift_type: 'regular',
      notes: ''
    });
    setShowModal(true);
  };

  // Open modal to edit shift
  const openEditShiftModal = (shift) => {
    setEditingShift(shift);
    setShiftForm({
      worker_id: shift.worker_id,
      date: shift.date,
      start_time: shift.start_time,
      end_time: shift.end_time,
      shift_type: shift.shift_type || 'regular',
      notes: shift.notes || ''
    });
    setShowModal(true);
  };

  // Handle cell click
  const handleCellClick = (workerId, date) => {
    const existingShift = getShiftForCell(workerId, date);
    if (existingShift) {
      openEditShiftModal(existingShift);
    } else {
      openAddShiftModal(workerId, date);
    }
  };

  // Save shift
  const saveShift = async () => {
    if (!shiftForm.worker_id || !shiftForm.date) {
      toast.error('Worker and date are required');
      return;
    }

    try {
      if (editingShift) {
        await api.put(`/schedules/shifts/${editingShift.id}`, shiftForm);
        toast.success('Shift updated');
      } else {
        await api.post('/schedules/shifts', shiftForm);
        toast.success('Shift created');
      }
      setShowModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save shift');
    }
  };

  // Delete shift
  const deleteShift = async (shiftId) => {
    if (!window.confirm('Delete this shift?')) return;
    
    try {
      await api.delete(`/schedules/shifts/${shiftId}`);
      toast.success('Shift deleted');
      setShowModal(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete shift');
    }
  };

  // ========== DRAG AND DROP ==========
  
  // Start dragging a shift
  const handleDragStart = (e, shift) => {
    setDraggedShift(shift);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', shift.id);
    // Add visual feedback
    e.target.style.opacity = '0.5';
  };

  // End dragging
  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedShift(null);
    setDragOverCell(null);
  };

  // Allow drop on cell
  const handleDragOver = (e, workerId, date) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverCell({ workerId, date });
  };

  // Handle drop on cell
  const handleDrop = async (e, targetWorkerId, targetDate) => {
    e.preventDefault();
    setDragOverCell(null);
    
    if (!draggedShift) return;
    
    // Check if dropping on same cell
    if (draggedShift.worker_id === targetWorkerId && draggedShift.date === targetDate) {
      setDraggedShift(null);
      return;
    }
    
    // Check if target cell already has a shift
    const existingShift = getShiftForCell(targetWorkerId, targetDate);
    if (existingShift) {
      toast.error('This cell already has a shift. Delete it first or drop elsewhere.');
      setDraggedShift(null);
      return;
    }
    
    // Update the shift
    try {
      await api.put(`/schedules/shifts/${draggedShift.id}`, {
        ...draggedShift,
        worker_id: targetWorkerId,
        date: targetDate
      });
      
      const worker = workers.find(w => w.id === targetWorkerId);
      toast.success(`Shift moved to ${worker?.full_name || 'worker'} on ${new Date(targetDate).toLocaleDateString()}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to move shift');
    }
    
    setDraggedShift(null);
  };

  // Leave drag over
  const handleDragLeave = () => {
    setDragOverCell(null);
  };

  // Calculate total hours per worker
  const getWorkerTotalHours = (workerId) => {
    return shifts
      .filter(s => s.worker_id === workerId)
      .reduce((total, s) => total + (s.hours || 0), 0);
  };

  // Calculate total shifts per day
  const getDayTotalShifts = (date) => {
    return shifts.filter(s => s.date === date).length;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-8 h-8 animate-spin text-[var(--primary)]" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="shift-calendar-view">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[var(--primary)]" />
            Shift Calendar
          </h2>
          <p className="text-sm text-[var(--text-muted)]">
            Click to add • Drag to move shifts
          </p>
        </div>
        
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => navigateWeek(-1)}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={goToToday}>
            Today
          </Button>
          <span className="font-medium min-w-[200px] text-center">
            {new Date(currentWeekStart).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {' '}
            {new Date(new Date(currentWeekStart).setDate(new Date(currentWeekStart).getDate() + 6)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </span>
          <Button variant="outline" size="sm" onClick={() => navigateWeek(1)}>
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button onClick={() => openAddShiftModal()} className="btn-primary" size="sm">
            <Plus className="w-4 h-4 mr-1" />
            Add Shift
          </Button>
        </div>
      </div>

      {/* Quick Templates Bar */}
      <div className="flex items-center gap-3 p-3 bg-[var(--background-subtle)] rounded-lg">
        <span className="text-sm font-medium text-[var(--text-muted)]">Quick Templates:</span>
        <div className="flex items-center gap-2">
          {Object.entries(SHIFT_TEMPLATES).map(([key, template]) => {
            const Icon = template.icon;
            return (
              <button
                key={key}
                onClick={() => {
                  // Open modal with template pre-filled
                  setEditingShift(null);
                  setShiftForm({
                    worker_id: '',
                    date: currentWeekStart,
                    start_time: template.start_time,
                    end_time: template.end_time,
                    shift_type: 'regular',
                    notes: ''
                  });
                  setShowModal(true);
                }}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium text-white ${template.color} hover:opacity-90 transition-opacity`}
                title={template.description}
                data-testid={`template-${key}`}
              >
                <Icon className="w-4 h-4" />
                {template.name}
              </button>
            );
          })}
        </div>
        <span className="text-xs text-[var(--text-muted)] ml-auto">
          Drag shifts to reschedule
        </span>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 text-sm flex-wrap">
        <span className="text-[var(--text-muted)]">Shift Types:</span>
        {Object.entries(SHIFT_COLORS).map(([type, colors]) => (
          <div key={type} className="flex items-center gap-1">
            <div className={`w-3 h-3 rounded ${colors.bg}`}></div>
            <span className="capitalize">{type.replace('_', ' ')}</span>
          </div>
        ))}
        <div className="flex items-center gap-1 ml-4 text-[var(--text-muted)]">
          <GripVertical className="w-4 h-4" />
          <span>= Drag handle</span>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="card overflow-hidden border">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[800px]">
            {/* Header Row */}
            <thead>
              <tr className="bg-[var(--background-subtle)]">
                <th className="sticky left-0 z-10 bg-[var(--background-subtle)] p-3 text-left min-w-[200px] border-r">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Workers ({workers.length})
                  </div>
                </th>
                {weekDays.map(day => (
                  <th 
                    key={day.date} 
                    className={`p-3 text-center min-w-[120px] ${day.isToday ? 'bg-[var(--primary)]/10' : ''}`}
                  >
                    <div className={`${day.isToday ? 'text-[var(--primary)] font-bold' : ''}`}>
                      <p className="text-sm">{day.dayName}</p>
                      <p className={`text-2xl ${day.isToday ? '' : 'text-[var(--text-muted)]'}`}>{day.dayNum}</p>
                    </div>
                    {getDayTotalShifts(day.date) > 0 && (
                      <p className="text-xs text-[var(--text-muted)] mt-1">
                        {getDayTotalShifts(day.date)} shifts
                      </p>
                    )}
                  </th>
                ))}
                <th className="p-3 text-center min-w-[80px] bg-[var(--background-subtle)] border-l">
                  Total Hrs
                </th>
              </tr>
            </thead>
            
            {/* Worker Rows */}
            <tbody className="divide-y">
              {workers.length === 0 ? (
                <tr>
                  <td colSpan={9} className="p-8 text-center text-[var(--text-muted)]">
                    <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No workers found</p>
                    <p className="text-sm">Add workers to start scheduling shifts</p>
                  </td>
                </tr>
              ) : (
                workers.map(worker => {
                  const totalHours = getWorkerTotalHours(worker.id);
                  const isOvertime = totalHours > 40;
                  
                  return (
                    <tr key={worker.id} className="hover:bg-[var(--background-subtle)]/50">
                      {/* Worker Name */}
                      <td className="sticky left-0 z-10 bg-[var(--background-paper)] p-3 border-r">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-[var(--primary)]/10 flex items-center justify-center">
                            <span className="font-semibold text-[var(--primary)]">
                              {worker.full_name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?'}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium">{worker.full_name}</p>
                            <p className="text-xs text-[var(--text-muted)]">{worker.email}</p>
                          </div>
                        </div>
                      </td>
                      
                      {/* Day Cells */}
                      {weekDays.map(day => {
                        const shift = getShiftForCell(worker.id, day.date);
                        const colors = shift ? SHIFT_COLORS[shift.shift_type] || SHIFT_COLORS.regular : null;
                        const isDragOver = dragOverCell?.workerId === worker.id && dragOverCell?.date === day.date;
                        
                        return (
                          <td 
                            key={day.date}
                            className={`p-2 text-center border-l transition-all ${
                              day.isToday ? 'bg-[var(--primary)]/5' : ''
                            } ${isDragOver ? 'bg-green-100 ring-2 ring-green-500 ring-inset' : 'hover:bg-[var(--primary)]/10'} ${
                              !shift ? 'cursor-pointer' : ''
                            }`}
                            onClick={() => !shift && handleCellClick(worker.id, day.date)}
                            onDragOver={(e) => handleDragOver(e, worker.id, day.date)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, worker.id, day.date)}
                            data-testid={`shift-cell-${worker.id}-${day.date}`}
                          >
                            {shift ? (
                              <motion.div
                                initial={{ scale: 0.8, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                draggable
                                onDragStart={(e) => handleDragStart(e, shift)}
                                onDragEnd={handleDragEnd}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openEditShiftModal(shift);
                                }}
                                className={`${colors.bg} ${colors.text} rounded-lg p-2 text-xs shadow-sm hover:shadow-md transition-all cursor-grab active:cursor-grabbing group relative`}
                                data-testid={`shift-block-${shift.id}`}
                              >
                                {/* Drag Handle */}
                                <div className="absolute top-1 right-1 opacity-50 group-hover:opacity-100">
                                  <GripVertical className="w-3 h-3" />
                                </div>
                                <div className="font-semibold">
                                  {shift.start_time} - {shift.end_time}
                                </div>
                                <div className="text-[10px] opacity-90">
                                  {shift.hours}h • {shift.shift_type}
                                </div>
                              </motion.div>
                            ) : (
                              <div className="h-14 flex items-center justify-center text-gray-300 hover:text-[var(--primary)] transition-colors">
                                <Plus className="w-5 h-5" />
                              </div>
                            )}
                          </td>
                        );
                      })}
                      
                      {/* Total Hours */}
                      <td className="p-3 text-center border-l bg-[var(--background-subtle)]/50">
                        <span className={`font-bold text-lg ${
                          isOvertime ? 'text-orange-500' : totalHours >= 35 ? 'text-amber-500' : 'text-green-500'
                        }`}>
                          {totalHours}h
                        </span>
                        {isOvertime && (
                          <p className="text-xs text-orange-500">Overtime</p>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="card p-4 text-center">
          <p className="text-3xl font-bold text-[var(--primary)]">{shifts.length}</p>
          <p className="text-sm text-[var(--text-muted)]">Total Shifts</p>
        </div>
        <div className="card p-4 text-center">
          <p className="text-3xl font-bold text-blue-500">
            {shifts.reduce((sum, s) => sum + (s.hours || 0), 0)}h
          </p>
          <p className="text-sm text-[var(--text-muted)]">Total Hours</p>
        </div>
        <div className="card p-4 text-center">
          <p className="text-3xl font-bold text-green-500">
            {new Set(shifts.map(s => s.worker_id)).size}
          </p>
          <p className="text-sm text-[var(--text-muted)]">Workers Scheduled</p>
        </div>
        <div className="card p-4 text-center">
          <p className="text-3xl font-bold text-purple-500">
            {shifts.filter(s => s.shift_type === 'overtime').length}
          </p>
          <p className="text-sm text-[var(--text-muted)]">Overtime Shifts</p>
        </div>
      </div>

      {/* Add/Edit Shift Modal */}
      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-[var(--background-paper)] rounded-xl shadow-2xl w-full max-w-md"
              data-testid="shift-modal"
            >
              <div className="p-4 border-b flex items-center justify-between">
                <h2 className="text-lg font-bold flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  {editingShift ? 'Edit Shift' : 'Add New Shift'}
                </h2>
                <Button variant="ghost" size="sm" onClick={() => setShowModal(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="p-4 space-y-4">
                {/* Quick Templates */}
                {!editingShift && (
                  <div>
                    <label className="block text-sm font-medium mb-2">Quick Templates</label>
                    <div className="grid grid-cols-4 gap-2">
                      {Object.entries(SHIFT_TEMPLATES).map(([key, template]) => {
                        const Icon = template.icon;
                        const isActive = shiftForm.start_time === template.start_time && 
                                        shiftForm.end_time === template.end_time;
                        return (
                          <button
                            key={key}
                            type="button"
                            onClick={() => applyTemplate(key)}
                            className={`flex flex-col items-center gap-1 p-2 rounded-lg text-xs font-medium transition-all ${
                              isActive 
                                ? `${template.color} text-white` 
                                : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                            }`}
                            title={template.description}
                          >
                            <Icon className="w-4 h-4" />
                            {template.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Worker */}
                <div>
                  <label className="block text-sm font-medium mb-1">Worker *</label>
                  <select
                    value={shiftForm.worker_id}
                    onChange={(e) => setShiftForm(prev => ({ ...prev, worker_id: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border border-[var(--border-primary)] bg-[var(--background-paper)]"
                    data-testid="shift-worker-select"
                  >
                    <option value="">Select Worker</option>
                    {workers.map(w => (
                      <option key={w.id} value={w.id}>{w.full_name}</option>
                    ))}
                  </select>
                </div>

                {/* Date */}
                <div>
                  <label className="block text-sm font-medium mb-1">Date *</label>
                  <Input
                    type="date"
                    value={shiftForm.date}
                    onChange={(e) => setShiftForm(prev => ({ ...prev, date: e.target.value }))}
                    data-testid="shift-date-input"
                  />
                </div>

                {/* Time Range */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Start Time *</label>
                    <Input
                      type="time"
                      value={shiftForm.start_time}
                      onChange={(e) => setShiftForm(prev => ({ ...prev, start_time: e.target.value }))}
                      data-testid="shift-start-time"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">End Time *</label>
                    <Input
                      type="time"
                      value={shiftForm.end_time}
                      onChange={(e) => setShiftForm(prev => ({ ...prev, end_time: e.target.value }))}
                      data-testid="shift-end-time"
                    />
                  </div>
                </div>

                {/* Shift Type */}
                <div>
                  <label className="block text-sm font-medium mb-1">Shift Type</label>
                  <div className="grid grid-cols-3 gap-2">
                    {Object.entries(SHIFT_COLORS).map(([type, colors]) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => setShiftForm(prev => ({ ...prev, shift_type: type }))}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                          shiftForm.shift_type === type
                            ? `${colors.bg} ${colors.text}`
                            : `${colors.light} hover:opacity-80`
                        }`}
                      >
                        {type.replace('_', ' ')}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium mb-1">Notes</label>
                  <textarea
                    value={shiftForm.notes}
                    onChange={(e) => setShiftForm(prev => ({ ...prev, notes: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border border-[var(--border-primary)] bg-[var(--background-paper)] resize-none"
                    rows={2}
                    placeholder="Optional notes..."
                    data-testid="shift-notes"
                  />
                </div>
              </div>

              {/* Modal Actions */}
              <div className="p-4 border-t flex items-center justify-between">
                {editingShift ? (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => deleteShift(editingShift.id)}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete
                  </Button>
                ) : (
                  <div />
                )}
                <div className="flex items-center gap-2">
                  <Button variant="outline" onClick={() => setShowModal(false)}>
                    Cancel
                  </Button>
                  <Button onClick={saveShift} className="btn-primary">
                    <Check className="w-4 h-4 mr-1" />
                    {editingShift ? 'Update' : 'Create'} Shift
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ShiftCalendarView;
